import React, {
  useContext,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { ReactComponent as Clock } from 'assets/icons/clock.svg';
import { ReactComponent as Check } from 'assets/icons/check.svg';
import { ReactComponent as Exclamation } from 'assets/icons/exclamation-triangle.svg';

import Loader from 'components/Loader';
import ActionPanel from 'components/ActionPanel';
import DashboardContainer from 'components/DashboardContainer';
import CardWithAction from 'components/CardWithAction';
import Insights from 'pages/Insights';

import PATHS from 'routes/paths';
import {
  currentCompanySelector,
  generateHopUrl,
  updateKycStatus,
} from 'redux/slices/company';
import { fundraiserSelectors } from 'redux/slices/fundraiser';

import { welcome as TEXT } from 'locales/en';
import { IN_REVIEW, PASSED, KYC_STATUS } from 'utils/kycVerifications';
import { toStartCase } from 'utils/transform';
import { logCtaWithLabel, logClickFundraiser } from 'utils/ga';
import { BrandContext } from 'utils/brand-context';

const renderKycStatus = (status) => {
  let Icon = Clock;
  if (KYC_STATUS[status] === IN_REVIEW) {
    Icon = Clock;
  } else if (KYC_STATUS[status] === PASSED) {
    Icon = Check;
  } else {
    Icon = Exclamation;
  }

  return (
    <p
      className={`text-sm inline-flex font-semibold kyc_${KYC_STATUS[status][0]}`}
    >
      <Icon className="w-5 h-5 mr-2 fill-current" />
      {KYC_STATUS[status]}
    </p>
  );
};

const Welcome = () => {
  const brand = useContext(BrandContext);
  const dispatch = useDispatch();
  const currentCompany = useSelector(currentCompanySelector);
  const hop_error = useSelector((state) => state.company.hop_error || {});
  const hasFundraiser = useSelector(fundraiserSelectors.selectTotal);
  const timer = useRef();
  const kycApproved = currentCompany.account_holder_kyc_just_approved;
  const loading = useSelector((state) => state.company.loading);
  const storeLoading = useSelector((state) => state.javelinStore.loading);
  const fundraiserLoading = useSelector((state) => state.fundraiser.loading);

  const displayFullPageLoader = loading || storeLoading || fundraiserLoading;
  const displayKYCStatus = useMemo(
    () => !!currentCompany.account_holder_verifications?.length,
    [currentCompany.account_holder_verifications]
  );

  const displayInsights = useMemo(
    () => hasFundraiser && !displayKYCStatus,
    [displayKYCStatus, hasFundraiser]
  );

  useEffect(() => {
    if (displayInsights) {
      document.title = 'Fundraiser Insights | Javelin';
    } else {
      document.title = `${TEXT.header1(brand.name)} | Javelin`;
    }
  }, [displayInsights, brand.name]);

  const _generateHopUrl = useCallback(async () => {
    const { payload } = await dispatch(
      generateHopUrl({ displayRedirection: false })
    );
    if (!!payload.url) {
      window.location.replace(payload.url);
    }
  }, [dispatch]);

  useEffect(() => {
    if (
      hop_error.status === 400 &&
      hop_error.message?.indexOf('Failed to retrieve account holder') > -1
    ) {
      timer.current = setInterval(() => {
        _generateHopUrl();
      }, 5000);
    } else {
      if (timer.current) clearInterval(timer.current);
    }
    return () => clearInterval(timer.current);
  }, [_generateHopUrl, hop_error]);

  const onDismiss = () => {
    dispatch(updateKycStatus(currentCompany.id));
  };

  if (displayFullPageLoader) return <Loader />;

  return displayInsights ? (
    <Insights />
  ) : (
    <>
      <DashboardContainer
        header={TEXT.header1(brand.name)}
        subheader={TEXT.header1Sub}
        transitionAppear={false}
      >
        <div className="flex flex-col w-full">
          {displayKYCStatus && (
            <CardWithAction
              title={
                hasFundraiser ? TEXT.complete : `${TEXT.step1} ${TEXT.complete}`
              }
              subtitle={TEXT.step1Subtitle}
              cta={TEXT.cta1}
              onClick={() => {
                logCtaWithLabel('complete_setup');
                _generateHopUrl();
              }}
              className="flex flex-col lg:flex-col h-full"
            >
              <div className="mt-5 mb-7">
                {currentCompany.account_holder_verifications.map((item) => (
                  <div
                    data-testid="verification-errors"
                    key={item.type}
                    className="flex w-full bg-javelin-gray-lightest h-14 lg:h-20 justify-between items-center px-4 mb-2"
                  >
                    <p className="font-semibold text-sm text-javelin-gray-dark text-opacity-80">
                      {toStartCase(item.type)}
                    </p>
                    {renderKycStatus(item.status)}
                  </div>
                ))}
              </div>
            </CardWithAction>
          )}
          {kycApproved && (
            <ActionPanel
              testId="success-panel"
              onClick={() => {
                logCtaWithLabel('dismiss_profile_approved');
                onDismiss();
              }}
              actionText={TEXT.dismiss}
              text={TEXT.approved}
            />
          )}
          {!hasFundraiser && (
            <CardWithAction
              title={
                displayKYCStatus
                  ? `${TEXT.step2} ${TEXT.createFirst}`
                  : TEXT.createFirst
              }
              subtitle={TEXT.step2Subtitle}
              cta={TEXT.cta2}
              to={PATHS.fundraisersNew}
              callback={logClickFundraiser}
            />
          )}
        </div>
      </DashboardContainer>
    </>
  );
};

export default Welcome;
